import { LoadingState } from "@/components/UIComponents";
import { ServiceFactory } from "@/services";
import type { AuthData, Employee } from "@/types/employee";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const employeesService = ServiceFactory.getEmployeesService();

/**
 * The `HUBS_EMPLOYEE_ROLE` reference options, by id.
 *
 * Negative ids on purpose: these are Hubs roles mirrored into the reference
 * table, not appointment reference data. Verified against
 * `/api/refOptions?code=HUBS_EMPLOYEE_ROLE`.
 */
const EMPLOYEE_ROLE_IDS = {
  ADMIN: -3,
  GROOMING: -4,
  SUPPORT: -7,
} as const;
interface AuthProviderProps {
  children: ReactNode;
}

interface AuthContextValue {
  token: string | null;
  isLoading: boolean;
  data: AuthData | null;
  isAdmin: boolean;
  isSupport: boolean;
  /**
   * Whether this employee may open every backoffice tool, including the
   * managers-only ones.
   *
   * Administrators and support: those tools are app-wide (one banner set and
   * one news feed for every store, so not a store role's to change) or
   * sensitive.
   */
  canManageContent: boolean;
  /**
   * Whether this employee may open the backoffice tools a store role may use,
   * based on the employee's primary role.
   *
   * False until the employee loads, and when it fails to, so a failed lookup
   * opens nothing.
   */
  canUseSharedTools: boolean;
  /** Metrics are denied when Grooming appears in any of the employee's roles. */
  canViewMetrics: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getAuthKey = (): string | null => {
  const envValue = import.meta.env?.VITE_AUTH_TOKEN;
  if (typeof envValue === "string" && envValue.length > 0) {
    return envValue;
  }

  return null;
};

const fetchEmployee = async (): Promise<Employee> => {
  const employeeIdFromEnv = import.meta.env?.VITE_SPECIFIC_EMPLOYEE;
  if (employeeIdFromEnv) {
    const employee = await employeesService.getEmployeeById(employeeIdFromEnv);
    return employee;
  }
  const employee = await employeesService.getCurrentEmployee();
  return employee;
};

const saveJwtInMemory = (jwt: string): void => {
  localStorage.setItem("authKey", jwt);
};

const initialState: AuthContextValue = {
  token: "",
  isLoading: true,
  data: null,
  isAdmin: false,
  isSupport: false,
  canManageContent: false,
  canUseSharedTools: false,
  canViewMetrics: false,
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const authKeyFromUrl = urlSearchParams.get("authorization");

  const [state, setState] = useState<AuthContextValue>(initialState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (state.token === authKeyFromUrl) return;
      setLoading(true);

      const authKey = authKeyFromUrl ?? getAuthKey();

      if (authKey) saveJwtInMemory(authKey);

      try {
        const employee = await fetchEmployee();
        const isAdmin = employee.role.id === EMPLOYEE_ROLE_IDS.ADMIN;
        const isSupport = employee.role.id === EMPLOYEE_ROLE_IDS.SUPPORT;
        const isGrooming = employee.role.id === EMPLOYEE_ROLE_IDS.GROOMING;
        const hasGroomingRole =
          isGrooming ||
          (employee.roles ?? []).some(
            role => role.id === EMPLOYEE_ROLE_IDS.GROOMING,
          );

        setState({
          token: authKey,
          isLoading: false,
          data: employee,
          isAdmin,
          isSupport,
          canManageContent: isAdmin || isSupport,
          canUseSharedTools: !isGrooming,
          canViewMetrics: !hasGroomingRole,
        });
      } catch (error) {
        console.error("Error loading auth context", error);
        setState({ ...initialState, isLoading: false });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authKeyFromUrl]);

  const value = useMemo<AuthContextValue>(() => state, [state]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <LoadingState placement="viewport" label="A preparar aplicação…" />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export type { AuthContextValue };
